import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, BookOpen, Clock, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ComprehensionQuestion, 
  ComprehensionResponse, 
  AssessmentUIState 
} from '@/types/comprehension';
import { comprehensionService } from '@/services/comprehension-service';
import { useAuth } from '@/components/auth/AuthProvider';
import { useProfile } from '@/hooks/useProfile';

interface ComprehensionAssessmentProps {
  onComplete: () => void;
  onPause?: () => void; // New prop for pause functionality
  resumeSession?: boolean; // New prop to indicate if resuming
}

export function ComprehensionAssessment({ onComplete, onPause, resumeSession = false }: ComprehensionAssessmentProps) {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [questions, setQuestions] = useState<ComprehensionQuestion[]>([]);
  const [state, setState] = useState<AssessmentUIState>({
    currentQuestionIndex: 0,
    totalQuestions: 0,
    responses: [],
    timeStarted: new Date().toISOString(),
    isLoading: true,
    isSubmitting: false
  });
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [questionStartTime, setQuestionStartTime] = useState<Date>(new Date());
  const [questionStartTimes, setQuestionStartTimes] = useState<Record<number, Date>>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const loadingRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls in React Strict Mode
    if (loadingRef.current) return;
    loadQuestions();
  }, [profile]); // Add profile as dependency

  // Update selected option when question index changes
  useEffect(() => {
    if (questions.length > 0 && state.currentQuestionIndex < questions.length) {
      const currentQuestionId = questions[state.currentQuestionIndex].id;
      const currentResponse = state.responses.find(
        response => response.question_id === currentQuestionId
      );
      
      if (currentResponse) {
        setSelectedOption(currentResponse.selected_option_id);
      } else {
        setSelectedOption('');
      }
    }
  }, [state.currentQuestionIndex, state.responses, questions]);

  const loadQuestions = async () => {
    if (!user?.id || loadingRef.current) return;
    
    loadingRef.current = true;
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      // Check if resuming an existing session
      if (resumeSession) {
        const savedSession = comprehensionService.getSavedAssessmentSession(user.id);
        if (savedSession) {
          // Resume from saved session
          setQuestions(savedSession.questions);
          setState(prev => ({
            ...prev,
            currentQuestionIndex: savedSession.currentQuestionIndex,
            totalQuestions: savedSession.questions.length,
            responses: savedSession.responses,
            timeStarted: savedSession.timeStarted,
            isLoading: false
          }));
          
          // Restore question start times
          const startTimes: Record<number, Date> = {};
          Object.entries(savedSession.questionStartTimes).forEach(([index, timeStr]) => {
            startTimes[parseInt(index)] = new Date(timeStr);
          });
          setQuestionStartTimes(startTimes);
          
          // Set current question start time
          const currentIndex = savedSession.currentQuestionIndex;
          if (startTimes[currentIndex]) {
            setQuestionStartTime(startTimes[currentIndex]);
          } else {
            const now = new Date();
            setQuestionStartTime(now);
            setQuestionStartTimes(prev => ({ ...prev, [currentIndex]: now }));
          }
          
          // Restore selected option for current question
          if (savedSession.selectedOption) {
            setSelectedOption(savedSession.selectedOption);
          }
          
          // Restore the assessment ID in the service for submission
          if (savedSession.assessmentId) {
            comprehensionService.setCurrentAssessmentId(savedSession.assessmentId);
          } else {
            // Fallback: if no assessmentId in session, generate a new one or restart assessment
            console.warn('No assessmentId found in saved session. This may cause submission issues.');
          }
          
          loadingRef.current = false;
          return;
        }
      }
      
      // Get class_id from profile for new assessment
      const classId = profile?.student_details?.class_id;
      
      if (!classId) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'No class information found in your profile. Please complete your profile first.'
        }));
        loadingRef.current = false;
        return;
      }
      
      const questionData = await comprehensionService.getAssessmentQuestions(classId);
      setQuestions(questionData);
      setState(prev => ({
        ...prev,
        totalQuestions: questionData.length,
        isLoading: false
      }));
      
      // Start timing for the first question
      const now = new Date();
      setQuestionStartTime(now);
      setQuestionStartTimes({ 0: now });
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load assessment questions'
      }));
    } finally {
      loadingRef.current = false;
    }
  };

  const saveCurrentSession = () => {
    if (!user?.id || questions.length === 0) return;
    
    const classId = profile?.student_details?.class_id;
    if (!classId) return;
    
    // Convert question start times to strings for serialization
    const startTimesStr: Record<number, string> = {};
    Object.entries(questionStartTimes).forEach(([index, date]) => {
      startTimesStr[parseInt(index)] = date.toISOString();
    });
    
    const session = {
      assessmentId: comprehensionService.getCurrentAssessmentId() || '',
      questions,
      responses: state.responses,
      currentQuestionIndex: state.currentQuestionIndex,
      selectedOption, // Save current selected option
      timeStarted: state.timeStarted,
      questionStartTimes: startTimesStr,
      userId: user.id,
      classId,
      lastUpdated: new Date().toISOString()
    };
    
    comprehensionService.saveAssessmentSession(session);
  };

  const handlePause = () => {
    // Save current session state
    saveCurrentSession();
    
    // Call the onPause callback if provided
    if (onPause) {
      onPause();
    }
  };

  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
    
    // Only update the selectedOption state, don't save response yet
    // Response will be saved when user clicks Next/Previous
    
    // Auto-save session when user selects an option (for selectedOption state)
    setTimeout(() => saveCurrentSession(), 100);
  };

  const handleNext = () => {
    if (!selectedOption) return;

    const currentQuestion = questions[state.currentQuestionIndex];
    const selectedOptionData = currentQuestion.options.find(opt => opt.id === selectedOption);
    
    if (!selectedOptionData) return;

    // Calculate time taken for this question
    const startTime = questionStartTimes[state.currentQuestionIndex] || questionStartTime;
    const timeTaken = Math.round((new Date().getTime() - startTime.getTime()) / 1000);

    // Create or update response for this question
    const existingResponseIndex = state.responses.findIndex(
      response => response.question_id === currentQuestion.id
    );

    let newResponses;
    if (existingResponseIndex !== -1) {
      // Update existing response
      newResponses = [...state.responses];
      newResponses[existingResponseIndex] = {
        ...newResponses[existingResponseIndex],
        selected_option_id: selectedOption,
        score: selectedOptionData.score,
        time_taken: timeTaken,
        responded_at: new Date().toISOString()
      };
    } else {
      // Create new response
      const response: ComprehensionResponse = {
        question_id: currentQuestion.id,
        selected_option_id: selectedOption,
        score: selectedOptionData.score,
        time_taken: timeTaken,
        responded_at: new Date().toISOString()
      };
      newResponses = [...state.responses, response];
    }
    
    if (state.currentQuestionIndex < questions.length - 1) {
      // Move to next question
      const nextIndex = state.currentQuestionIndex + 1;
      setState(prev => ({
        ...prev,
        currentQuestionIndex: nextIndex,
        responses: newResponses
      }));
      setSelectedOption('');
      
      // Start timing for the next question
      const nextQuestionStartTime = new Date();
      setQuestionStartTime(nextQuestionStartTime);
      setQuestionStartTimes(prev => ({ ...prev, [nextIndex]: nextQuestionStartTime }));
      
      // Save session state after updating
      setTimeout(() => saveCurrentSession(), 100);
    } else {
      // Assessment complete
      setState(prev => ({
        ...prev,
        responses: newResponses
      }));
      // Clear session before submitting
      if (user?.id) {
        comprehensionService.clearAssessmentSession(user.id);
      }
      submitAssessment(newResponses);
    }
  };

  const handlePrevious = () => {
    if (state.currentQuestionIndex <= 0) return;
    
    let updatedResponses = state.responses;
    
    // Save current question's response if user has selected an option
    if (selectedOption) {
      const currentQuestion = questions[state.currentQuestionIndex];
      const selectedOptionData = currentQuestion.options.find(opt => opt.id === selectedOption);
      
      if (selectedOptionData) {
        const startTime = questionStartTimes[state.currentQuestionIndex] || questionStartTime;
        const timeTaken = Math.round((new Date().getTime() - startTime.getTime()) / 1000);

        const existingResponseIndex = state.responses.findIndex(
          response => response.question_id === currentQuestion.id
        );

        if (existingResponseIndex !== -1) {
          // Update existing response
          updatedResponses = [...state.responses];
          updatedResponses[existingResponseIndex] = {
            ...updatedResponses[existingResponseIndex],
            selected_option_id: selectedOption,
            score: selectedOptionData.score,
            time_taken: timeTaken,
            responded_at: new Date().toISOString()
          };
        } else {
          // Create new response
          const response: ComprehensionResponse = {
            question_id: currentQuestion.id,
            selected_option_id: selectedOption,
            score: selectedOptionData.score,
            time_taken: timeTaken,
            responded_at: new Date().toISOString()
          };
          updatedResponses = [...state.responses, response];
        }
      }
    }
    
    const prevIndex = state.currentQuestionIndex - 1;
    
    // Update state with new responses and question index
    setState(prev => ({
      ...prev,
      currentQuestionIndex: prevIndex,
      responses: updatedResponses
    }));
    
    // Set selected option based on previous response
    const previousQuestionId = questions[prevIndex]?.id;
    const previousResponse = updatedResponses.find(
      response => response.question_id === previousQuestionId
    );
    if (previousResponse) {
      setSelectedOption(previousResponse.selected_option_id);
    } else {
      setSelectedOption('');
    }
    
    // Update question start time for the previous question
    if (questionStartTimes[prevIndex]) {
      setQuestionStartTime(questionStartTimes[prevIndex]);
    } else {
      const now = new Date();
      setQuestionStartTime(now);
      setQuestionStartTimes(prev => ({ ...prev, [prevIndex]: now }));
    }
    
    // Save session state
    setTimeout(() => saveCurrentSession(), 100);
  };

  const submitAssessment = async (responses: ComprehensionResponse[]) => {
    if (!user?.id) return;

    setState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const result = await comprehensionService.submitAssessment(user.id, responses);
      
      if (result.success) {
        // Assessment submitted successfully - show success message
        setState(prev => ({ ...prev, isSubmitting: false }));
        setShowSuccessMessage(true);
      } else {
        setState(prev => ({
          ...prev,
          isSubmitting: false,
          error: result.error || 'Failed to submit assessment'
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        isSubmitting: false,
        error: 'Failed to submit assessment'
      }));
    }
  };

  const handleSuccessClick = () => {
    setShowSuccessMessage(false);
    onComplete();
  };

  const currentQuestion = questions[state.currentQuestionIndex];
  const isLastQuestion = state.currentQuestionIndex === questions.length - 1;
  
  // Progress should be based on unique questions answered
  const uniqueAnsweredQuestions = new Set(state.responses.map(response => response.question_id)).size;
  const progress = (uniqueAnsweredQuestions / questions.length) * 100;

  if (state.isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="text-sm">Loading assessment questions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (state.isSubmitting) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <div className="text-center space-y-3">
            <div className="flex justify-center">
              <CheckCircle className="h-12 w-12 text-green-500 animate-pulse" />
            </div>
            <h3 className="text-lg font-bold text-green-600">Assessment Complete!</h3>
            <p className="text-sm text-muted-foreground">
              Thank you for completing the comprehension assessment! Your responses are being processed to personalize your learning experience.
            </p>
            <div className="flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span className="text-sm">Submitting assessment...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (showSuccessMessage) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-green-600">Assessment Submitted Successfully!</h3>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Thank you for completing the comprehension assessment! Your responses have been submitted and will help us personalize your learning experience.
              </p>
              <p className="text-sm text-muted-foreground">
                You won't see your results, but our system will use your responses to provide explanations and content at your comprehension level.
              </p>
            </div>
            <Button 
              onClick={handleSuccessClick}
              className="mt-4"
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (state.error) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800 text-sm">
              {state.error}
            </AlertDescription>
          </Alert>
          <div className="mt-4 flex justify-center space-x-3">
            <Button onClick={loadQuestions} variant="outline" size="sm">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentQuestion) {
    return null;
  }

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="h-5 w-5 text-primary" />
              <div>
                <CardTitle className="text-lg">Comprehension Assessment</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Help us understand how you learn best
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge variant="secondary" className="px-3 py-1">
                <Clock className="h-3 w-3 mr-1" />
                Question {state.currentQuestionIndex + 1} of {questions.length}
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Progress</span>
          <span>{Math.round(progress)}% Complete</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Question */}
      <Card>
        <CardHeader>
          <div className="space-y-3">
            
            {/* Source excerpt for context */}
            {currentQuestion.source_excerpt && (
              <div className="bg-muted/50 p-4 rounded-lg border-l-4 border-primary/30">
                <p className="text-sm font-medium text-muted-foreground mb-2">Context:</p>
                <p className="text-sm italic leading-relaxed">
                  "{currentQuestion.source_excerpt}"
                </p>
              </div>
            )}
            
            <CardTitle className="text-lg leading-relaxed">
              {currentQuestion.question}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground mb-6">
            Select the answer that best matches your understanding level:
          </p>
          
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => (
              <Card 
                key={option.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedOption === option.id 
                    ? 'ring-2 ring-primary border-primary bg-primary/5' 
                    : 'hover:border-primary/50'
                }`}
                onClick={() => handleOptionSelect(option.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className={`
                      w-6 h-6 rounded-full border-2 flex items-center justify-center text-sm font-medium
                      ${selectedOption === option.id 
                        ? 'border-primary bg-primary text-primary-foreground' 
                        : 'border-gray-300 bg-white'
                      }
                    `}>
                      {String.fromCharCode(65 + index)}
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="font-medium leading-relaxed">
                        {option.text}
                      </p>
                      {option.explanation && (
                        <p className="text-sm text-muted-foreground">
                          {option.explanation}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div className="flex space-x-3">
              {state.currentQuestionIndex > 0 && (
                <Button 
                  variant="outline" 
                  onClick={handlePrevious}
                  className="min-w-24"
                >
                  Previous
                </Button>
              )}
            </div>
            <div className="flex space-x-3">
              {onPause && (
                <Button 
                  variant="outline" 
                  onClick={handlePause}
                  className="text-muted-foreground"
                >
                  Pause Assessment
                </Button>
              )}
              <Button 
                onClick={handleNext}
                disabled={!selectedOption}
                className="min-w-24"
              >
                {isLastQuestion ? 'Complete' : 'Next'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
